/**
 * État de rapprochement d'une écriture comptable face aux opérations du 8000.
 *
 * Jusqu'ici les listes n'affichaient qu'une coche, posée sur `income_spent` ou
 * `spending_fully_matched`. Une écriture rapprochée à 99 % et une écriture jamais touchée s'y
 * présentaient donc à l'identique : sans coche. Or la différence est ce qui intéresse celui qui
 * pointe — il cherche ce qu'il reste à faire, pas ce qui n'est pas fini.
 *
 * L'état se lit sur le reste à pointer, jamais sur le seul drapeau : c'est le reste que le 8000
 * décrémente à chaque rattachement, et c'est lui qui dit où en est réellement l'écriture.
 */

export const MATCHING_UNKNOWN = 'unknown';
export const MATCHING_NONE = 'none';
export const MATCHING_PARTIAL = 'partial';
export const MATCHING_FULL = 'full';
export const MATCHING_INCONSISTENT = 'inconsistent';

// Les deux bases ne tombent pas au franc près : le 5000 arrondit ses écritures à l'unité alors
// que les montants du 8000 portent les décimales des calculs de taxe et de marge. Un écart
// inférieur au franc ne dit rien de l'état du rapprochement.
const TOLERANCE = 1;

function toNumber(value) {
    if (value === undefined || value === null || value === '') return null;
    const parsed = typeof value === 'number' ? value : parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
}

/**
 * matchingState détermine l'état d'une écriture à partir de son montant, de son reste à pointer
 * et du drapeau que porte le document.
 *
 * Le reste absent n'est pas un zéro : les documents antérieurs à l'introduction du champ n'en
 * portent pas, et les traiter comme soldés afficherait une coche mensongère. Ils sont rendus
 * comme indéterminés, et la liste n'affiche alors aucune pastille plutôt qu'une fausse.
 */
export function matchingState(amount, remaining, flag) {
    const total = toNumber(amount);
    const rest = toNumber(remaining);

    if (total === null || total <= 0) return MATCHING_UNKNOWN;
    if (rest === null) return flag === true ? MATCHING_FULL : MATCHING_UNKNOWN;

    // Un reste négatif, ou supérieur au montant de l'écriture, ne décrit aucun état de
    // rapprochement possible : le document se contredit lui-même et demande un examen.
    if (rest < -TOLERANCE || rest > total + TOLERANCE) return MATCHING_INCONSISTENT;

    if (rest <= TOLERANCE) return MATCHING_FULL;
    if (Math.abs(rest - total) <= TOLERANCE) return flag === true ? MATCHING_FULL : MATCHING_NONE;
    return MATCHING_PARTIAL;
}

/** incomeMatchingState lit l'état d'une recette. */
export function incomeMatchingState(income) {
    if (!income) return MATCHING_UNKNOWN;
    return matchingState(income.income_amount, income.income_total_available, income.income_spent);
}

/** spendingMatchingState lit l'état d'une dépense. Son montant de référence est le TTC. */
export function spendingMatchingState(spending) {
    if (!spending) return MATCHING_UNKNOWN;
    const amount = toNumber(spending.spending_amountTTC);
    return matchingState(
        amount !== null && amount > 0 ? amount : spending.spending_amount,
        spending.spending_rest_to_match,
        spending.spending_fully_matched,
    );
}

/** remainingAmount rend le reste à pointer affichable, ou null s'il n'est pas exploitable. */
export function remainingAmount(amount, remaining) {
    const total = toNumber(amount);
    const rest = toNumber(remaining);
    if (total === null || rest === null) return null;
    if (rest < -TOLERANCE || rest > total + TOLERANCE) return rest;
    return rest <= TOLERANCE ? 0 : rest;
}

export function incomeRemaining(income) {
    return income ? remainingAmount(income.income_amount, income.income_total_available) : null;
}

export function spendingRemaining(spending) {
    if (!spending) return null;
    const amount = toNumber(spending.spending_amountTTC);
    return remainingAmount(
        amount !== null && amount > 0 ? amount : spending.spending_amount,
        spending.spending_rest_to_match,
    );
}

/** Libellés affichés, communs aux deux écrans. */
export const MATCHING_LABELS = {
    [MATCHING_FULL]: 'Entièrement pointée',
    [MATCHING_PARTIAL]: 'Partiellement pointée',
    [MATCHING_NONE]: 'Non pointée',
    [MATCHING_INCONSISTENT]: 'Incohérente',
    [MATCHING_UNKNOWN]: 'État inconnu',
};
